var _class, _descriptor, _descriptor2, _descriptor3, _descriptor4;
function _initializerDefineProperty(target, property, descriptor, context) { if (!descriptor) return; Object.defineProperty(target, property, { enumerable: descriptor.enumerable, configurable: descriptor.configurable, writable: descriptor.writable, value: descriptor.initializer ? descriptor.initializer.call(context) : void 0 }); }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : String(i); }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(target, property, decorators, descriptor, context) { var desc = {}; Object.keys(descriptor).forEach(function (key) { desc[key] = descriptor[key]; }); desc.enumerable = !!desc.enumerable; desc.configurable = !!desc.configurable; if ('value' in desc || desc.initializer) { desc.writable = true; } desc = decorators.slice().reverse().reduce(function (desc, decorator) { return decorator(target, property, desc) || desc; }, desc); if (context && desc.initializer !== void 0) { desc.value = desc.initializer ? desc.initializer.call(context) : void 0; desc.initializer = undefined; } if (desc.initializer === void 0) { Object.defineProperty(target, property, desc); desc = null; } return desc; }
function _initializerWarningHelper(descriptor, context) { throw new Error('Decorating class property failed. Please ensure that ' + 'transform-class-properties is enabled and runs after the decorators transform.'); }
import GraphQLClientError from '@ember-graphql-client/client/errors/graphql-client-error';
import GraphQLNetworkError from '@ember-graphql-client/client/errors/network-error';
import GraphQLCache from '@ember-graphql-client/client/utils/graphql-cache';
import GraphQLRequestClient from '@ember-graphql-client/client/utils/graphql-request-client';
import { isNetworkError } from '@ember-graphql-client/client/utils/is-network-error';
import { assert } from '@ember/debug';
import Service from '@ember/service';
import { buildWaiter } from '@ember/test-waiters';
import { tracked } from '@glimmer/tracking';
import { cached } from "ember-cached-decorator-polyfill";
import fetch from 'fetch';
import { ClientError, GraphQLClient } from 'graphql-request';
const waiter = buildWaiter('@ember-graphql-client/client:request-waiter');
let GraphQLService = (_class = class GraphQLService extends Service {
  get apiURL() {
    return this._apiURL;
  }
  set apiURL(apiURL) {
    this._apiURL = apiURL;
    assert('Trying to set apiURL on graphql will not do anything when the client has been overwritten', !this._client);
  }
  get headers() {
    return this._headers;
  }
  set headers(headers) {
    this._headers = headers;
    assert('Trying to set headers on graphql will not do anything when the client has been overwritten', !this._client);
  }
  get options() {
    return this._options;
  }
  set options(options) {
    this._options = options;
    assert('Trying to set options on graphql will not do anything when the client has been overwritten', !this._client);
  }
  get client() {
    // This is mostly for testing purposes
    if (this._client) {
      return this._client;
    }
    let {
      apiURL,
      options,
      headers
    } = this;
    assert('graphql: You have to define an apiURL', typeof apiURL === 'string');
    return this._createClient({
      apiURL,
      options,
      headers
    });
  }
  set client(client) {
    this._client = client;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  constructor(owner) {
    super(owner);
    _defineProperty(this, "cache", new GraphQLCache());
    _initializerDefineProperty(this, "_apiURL", _descriptor, this);
    _initializerDefineProperty(this, "_options", _descriptor2, this);
    _initializerDefineProperty(this, "_headers", _descriptor3, this);
    _initializerDefineProperty(this, "_client", _descriptor4, this);
    _defineProperty(this, "errorHandler", void 0);
    let config = owner.resolveRegistration('config:environment').graphql || {};
    this._options = config.options;
    this._apiURL = config.apiURL;
  }
  async query(options, cacheOptions, client = this.client) {
    let cachedResponse = options ? this._maybeUseCachedResponse(options, cacheOptions) : undefined;
    let promise = cachedResponse || client.query(options);

    // We cache the promise to avoid parallel network requests
    if (!cachedResponse) {
      this._maybeStoreCachedResponse(options, cacheOptions, promise);
    }
    let token = waiter.beginAsync();
    try {
      let response = await promise;

      // If the response is not just the cachedResponse, we try to cache it
      if (response !== cachedResponse) {
        this._maybeStoreCachedResponse(options, cacheOptions, response);
      }
      waiter.endAsync(token);
      return response;
    } catch (error) {
      waiter.endAsync(token);

      // Make sure to clear the cached response, to avoid having a rejected promise cached
      this._maybeClearCachedResponse(options, cacheOptions);
      throw this._handleError(error, {
        source: 'query'
      });
    }
  }
  async mutate(options, cacheOptions, client = this.client) {
    let response;
    let token = waiter.beginAsync();
    try {
      response = await client.mutate(options);
    } catch (error) {
      waiter.endAsync(token);
      throw this._handleError(error, {
        source: 'mutate'
      });
    }
    if (cacheOptions?.invalidateCache) {
      this.invalidateCache(cacheOptions.invalidateCache);
    }
    waiter.endAsync(token);
    return response;
  }
  invalidateCache(invalidateCache) {
    invalidateCache.forEach(({
      cacheEntity,
      cacheId
    }) => {
      this.invalidateSingleCache({
        cacheEntity,
        cacheId
      });
    });
  }
  invalidateSingleCache({
    cacheEntity,
    cacheId
  }) {
    let cache = this.cache.getCache(cacheEntity, cacheId);
    cache.clear();
  }
  _maybeUseCachedResponse(options, cacheOptions) {
    let cache = this._getCache(cacheOptions);
    if (!cache) {
      return;
    }
    let {
      cacheSeconds
    } = cacheOptions;
    let cachedResponse = cache.get(options);
    if (typeof cachedResponse === 'undefined') {
      return undefined;
    }
    if (typeof cacheSeconds === 'undefined') {
      return cachedResponse.response;
    }
    let now = Math.round(+new Date() / 1000);
    if (now - cacheSeconds <= cachedResponse.timestamp) {
      return cachedResponse.response;
    }
    return undefined;
  }
  _maybeStoreCachedResponse(options, cacheOptions, response) {
    let cache = this._getCache(cacheOptions);
    if (!cache) {
      return;
    }
    cache.set(options, response);
  }
  _maybeClearCachedResponse(options, cacheOptions) {
    let cache = this._getCache(cacheOptions);
    if (!cache) {
      return;
    }
    cache.remove(options);
  }
  _getCache(cacheOptions) {
    if (!cacheOptions) {
      return;
    }
    let {
      cacheEntity,
      cacheId
    } = cacheOptions;
    if (!cacheEntity) {
      return;
    }
    return this.cache.getCache(cacheEntity, cacheId);
  }
  _handleError(error, {
    source
  }) {
    let parsedError = error;

    // Network error, e.g. offline
    if (isNetworkError(error)) {
      parsedError = new GraphQLNetworkError();
    }
    if (error instanceof ClientError) {
      parsedError = new GraphQLClientError(error);
    }
    if (this.errorHandler) {
      let handledError = this.errorHandler(parsedError, {
        source,
        originalError: error
      });
      if (handledError !== false) {
        return handledError;
      }
    }
    return parsedError;
  }
  _createClient({
    apiURL,
    headers,
    options
  }) {
    let graphQLClient = new GraphQLClient(apiURL, Object.assign({
      headers,
      fetch
    }, options));
    return new GraphQLRequestClient(graphQLClient);
  }
}, (_descriptor = _applyDecoratedDescriptor(_class.prototype, "_apiURL", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "_options", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return {};
  }
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "_headers", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return {};
  }
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "_client", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "client", [cached], Object.getOwnPropertyDescriptor(_class.prototype, "client"), _class.prototype)), _class);
export { GraphQLService as default };
export { GraphQLClient } from 'graphql-request';

// DO NOT DELETE: this is how TypeScript knows how to look up your services.